import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { setLenis, getLenis } from "@/lib/lenis";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileActionBar from "@/components/MobileActionBar";
import ConciergeChat from "@/components/ConciergeChat";
import VillaEnquiryModal from "@/components/VillaEnquiryModal";
import HomePage from "@/pages/HomePage";
import ExcursionsPage from "@/pages/ExcursionsPage";
import RestaurantsPage from "@/pages/RestaurantsPage";
import ServicesPage from "@/pages/ServicesPage";
import TermsPage from "@/pages/TermsPage";
import { BlogList, BlogPost } from "@/pages/BlogPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import PaymentResult from "@/pages/PaymentResult";
import CartPage from "@/pages/CartPage";
import { CartProvider } from "@/context/CartContext";

function AuthCallback() {
  const { completeGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        completeGoogleLogin(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account", { replace: true });
      } catch {
        toast.error("Google sign-in failed — please try again");
        navigate("/auth", { replace: true });
      }
    })();
  }, [completeGoogleLogin, navigate]);
  return (
    <div className="min-h-screen grid place-items-center bg-sand" data-testid="auth-callback">
      <p className="text-ink/50 text-sm font-mono uppercase tracking-[0.2em]">Signing you in…</p>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Detect the OAuth session_id synchronously during render (prevents race with auth check)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/excursions" element={<ExcursionsPage />} />
      <Route path="/restaurants" element={<RestaurantsPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/account" element={<DashboardPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/payment/success" element={<PaymentResult kind="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult kind="cancel" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    setLenis(lenis);
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="min-h-screen bg-sand text-ink font-sans flex flex-col">
            <Navbar onEnquire={() => setEnquiryOpen(true)} />
            <main className="grow">
              <AppRoutes />
            </main>
            <Footer />
            <MobileActionBar onEnquire={() => setEnquiryOpen(true)} />
            <VillaEnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />
            <ConciergeChat />
          </div>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}
